import React from "react";
import DashboardCalculationHover from "./DashboardCalculationHover.jsx";
import DashboardLowLoadDialog from "./DashboardLowLoadDialog.jsx";
import DashboardKpiDialog from "./DashboardKpiDialog.jsx";
import { formatFleetPercent } from "../utils/fleetUtilizationData.js";
import { buildSparkline, resolveComparisonLabel, resolveMetricDelta, SPARKLINE_MAX_DAYS, SPARKLINE_MIN_DAYS } from "../utils/dashboardComparison.js";

const PER_TONNE = new Intl.NumberFormat("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const perTonne = (value) => (value == null ? "n/d" : `${PER_TONNE.format(value)} $/t`);
const dayMonth = (day) => (day?.dispatchDate ? `${day.dispatchDate.slice(8, 10)}/${day.dispatchDate.slice(5, 7)}` : "");

// Values come from the period summaries built by DashboardHome; `previous` is the same metric on the previous period.
const CELLS = [
  { key: "utilization", label: "Utilisation de la flotte", unit: "pts", higherIsBetter: true,
    value: (s) => s.fleetRetainedUtilizationPct, text: (s) => formatFleetPercent(s.fleetRetainedUtilizationPct), day: (d) => d.fleetUtilization?.retainedPct },
  { key: "tonnage", label: "Tonnage / dispatch", unit: "%", higherIsBetter: true,
    value: (s) => s.avgPlannedTonnageTons, text: (s) => s.avgPlannedTonnageLabel, day: (d) => d.plannedTonnageTons },
  { key: "cost", label: "Coût / tonne", unit: "%", higherIsBetter: false,
    value: (s) => s.realCostPerTonneCad, text: (s) => s.realCostPerTonneLabel,
    day: (d) => (d.realCostCad != null && d.plannedTonnageTons > 0 ? d.realCostCad / d.plannedTonnageTons : null),
    details: (s) => [
      ["Roulage (km)", perTonne(s.realCostDistancePerTonneCad)],
      ["Camion (temps)", perTonne(s.realCostTruckTimePerTonneCad)],
      ["Chauffeur", perTonne(s.realCostDriverPerTonneCad)],
      ["Par commande", s.realCostPerOrderLabel],
      ["Par heure de mission", s.realCostPerHourLabel],
      ...(s.realCostMissingTruckCount > 0 ? [["Camions sans taux, exclus", String(s.realCostMissingTruckCount)]] : []),
    ] },
  { key: "peak", label: "Remplissage maximal", unit: "pts", higherIsBetter: true,
    value: (s) => s.avgPeakLoadFillRatePct, text: (s) => s.avgPeakLoadFillRateLabel, day: (d) => d.avgPeakLoadFillRatePct },
  { key: "low-load", label: "Km à vide / faible charge", unit: "pts", higherIsBetter: false,
    value: (s) => s.lowLoadDistancePct, text: (s) => s.lowLoadDistancePctLabel, day: (d) => d.lowLoadDistancePct },
];

function periodRowLabel(period, summary, previous) {
  const days = (period?.days || []).filter((day) => day.hasDispatch);
  if (period?.key === "day") return `${previous ? "Veille" : "Hier"}${days[0] ? ` (${dayMonth(days[0])})` : ""}`;
  const count = summary?.dispatchDayCount || days.length;
  const range = days.length ? `, du ${dayMonth(days[0])} au ${dayMonth(days.at(-1))}` : "";
  return `${previous ? "Précédente" : "Période"} (${count} dispatch${count > 1 ? "s" : ""}${range})`;
}

export function Sparkline({ bars }) {
  if (!bars.some((bar) => bar.value !== null)) return <span className="dashboard-kpi-spark" aria-hidden="true" />;
  const gap = bars.length > 20 ? 1 : 2;
  const width = (72 - gap * (bars.length - 1)) / bars.length;
  return <svg className="dashboard-kpi-spark" viewBox="0 0 72 28" preserveAspectRatio="none" aria-hidden="true">
    {bars.map((bar, index) => bar.value === null ? null : <rect key={bar.date || index} x={index * (width + gap)} y={28 - 0.28 * bar.heightPct}
      width={width} height={0.28 * bar.heightPct} rx="1" data-selected={bar.selected ? "true" : undefined} />)}
  </svg>;
}

export default function DashboardKpiStrip({ period, summary, previousSummary = null, contextDays = [] }) {
  const [selectedKpiKey, setSelectedKpiKey] = React.useState(null);
  React.useEffect(() => { setSelectedKpiKey(null); }, [period.key]);
  const selectedCell = CELLS.find((cell) => cell.key === selectedKpiKey);
  const selectedDates = new Set((period.days || []).map((day) => day.dispatchDate));
  const barCount = period.key === "month" ? SPARKLINE_MAX_DAYS : SPARKLINE_MIN_DAYS;
  const previousPeriod = period.previous || null;
  return <section className="dashboard-kpi-strip" aria-label="Indicateurs clés">
    <div className="dashboard-kpi-grid rounded-[4px]">
      {CELLS.map((cell) => {
        const text = cell.text(summary);
        const delta = previousSummary ? resolveMetricDelta(cell.value(summary), cell.value(previousSummary), cell) : null;
        const rows = [[periodRowLabel(period, summary, false), text],
          previousSummary ? [periodRowLabel(previousPeriod, previousSummary, true), cell.text(previousSummary)] : ["Période précédente", "indisponible · écart masqué"],
          ...(cell.details ? cell.details(summary) : [])];
        return <DashboardCalculationHover key={cell.key} label={cell.label} value={text}
          onOpenDetails={() => setSelectedKpiKey(cell.key)}
          detailsOpen={selectedKpiKey !== null} hoverCard={{ content: <dl className="dashboard-kpi-rows">
          {rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
        </dl> }}>
          <div className="dashboard-kpi-cell" data-dashboard-kpi={cell.key}>
            <span className="dashboard-kpi-label">{cell.label}</span>
            <span className="dashboard-kpi-main">
              <span className="dashboard-kpi-value">{text}</span>
              <Sparkline bars={buildSparkline(contextDays, cell.day, selectedDates, barCount)} />
            </span>
            <span className="dashboard-kpi-delta">{delta ? <><span data-tone={delta.tone}>{delta.label}</span> <span className="dashboard-kpi-compare">{resolveComparisonLabel(period, previousSummary.dispatchDayCount)}</span></> : null}</span>
          </div>
        </DashboardCalculationHover>;
      })}
    </div>
    {selectedKpiKey === "low-load" && <DashboardLowLoadDialog period={period} summary={summary} onClose={() => setSelectedKpiKey(null)} />}
    {selectedCell && selectedKpiKey !== "low-load" && <DashboardKpiDialog cell={selectedCell} period={period} summary={summary} previousSummary={previousSummary} onClose={() => setSelectedKpiKey(null)} />}
  </section>;
}
